import React from 'react';
import {Paper, Stack, Tooltip, Typography, type TypographyProps} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {COLORS} from '~/style/colors-ts';

export type StatChipTooltip = {
    type: 'info' | 'warning' | 'error';
    text: string;
};

export type StatChipProps = {
    icon: {element: React.ReactElement; color: string};
    title: React.ReactNode;
    value?: React.ReactNode;
    description?: React.ReactNode;
    tooltip?: StatChipTooltip;
    className?: string;
    titleProps?: TypographyProps;
    valueProps?: TypographyProps;
    descriptionProps?: TypographyProps;
    colon?: boolean
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'grey'
    size?: 'small' | 'medium'
};

const TOOLTIP_COLORS: Record<StatChipTooltip['type'], string> = {
    info: COLORS.info.main,
    warning: COLORS.warning.main,
    error: COLORS.error.main,
};

const TOOLTIP_ICONS: Record<StatChipTooltip['type'], React.ElementType> = {
    info: InfoOutlinedIcon,
    warning: WarningAmberOutlinedIcon,
    error: ErrorOutlineIcon,
};

export function StatChip({
                             size = 'medium',
                             icon,
                             title,
                             value,
                             description,
                             tooltip,
                             className,
                             titleProps,
                             valueProps,
                             descriptionProps,
                             colon = true,
                             color,
                         }: StatChipProps) {
    const TooltipIcon = tooltip ? TOOLTIP_ICONS[tooltip.type] : null;
    const tooltipColor = tooltip ? TOOLTIP_COLORS[tooltip.type] : undefined;

    return (
        <Paper
            variant="outlined"
            className={className}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: size === 'small' ? 1 : 1.5,
                py: size === 'small' ? 0 : 0.75,
                borderRadius: '8px',
                ...((color) && {backgroundColor: COLORS[color][100], borderColor: COLORS[color][300]}),
            }}
        >
            <Stack direction="row" alignItems="center" gap="4px">
                <Typography component="span" sx={{display: 'inline-flex', color: icon.color, lineHeight: 0}}>
                    {React.cloneElement(icon.element, {
                        style: {width: 24, height: 24, ...icon.element.props.style},
                    })}
                </Typography>
                <Typography component="span" fontSize={13} color={color ? COLORS[color][700] : "text.primary"} {...titleProps}>
                    {title}{colon && ':'}
                </Typography>
                <Typography
                            component="span"
                            color="text.secondary"
                            aria-hidden
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'center',
                                lineHeight: 0,
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                >
                    {!colon && value && '·'}
                </Typography>
                <Typography component="span" fontSize={13.5} fontWeight={700} {...valueProps}>
                    {value}
                </Typography>
                {description != null && description !== '' && (
                    <>
                        <Typography
                            component="span"
                            color="text.secondary"
                            aria-hidden
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'center',
                                lineHeight: 0,
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                        >
                            ·
                        </Typography>
                        <Typography component="span" fontSize={13.5} color="grey.500" {...descriptionProps}>
                            {description}
                        </Typography>
                    </>
                )}
                {tooltip && TooltipIcon && (
                    <Tooltip
                        title={tooltip.text}
                        arrow
                    >
                        <TooltipIcon style={{color: tooltipColor, width: 16, height: 16}}/>
                    </Tooltip>
                )}
            </Stack>
        </Paper>
    );
}
